/**
 * Utility functions for creating aggregate fetcher results
 */

import type { CollectorMeta } from './createCollector';
import {
  collectGroupMetadata,
  combineGroupData,
  createGroupNext,
  createGroupReset,
  groupHasNext
} from './groupDataManager';
import type { SourceGroup } from './sourceGrouping';
import type { AggregateCollectorStatus } from './types';

export interface AggregateFetcherResult {
  id: string;
  status: AggregateCollectorStatus;
  data: unknown[];
  hasNext: boolean;
  next(): Promise<void>;
  reset(): void;
  sourcesStates?: CollectorMeta<unknown>[];
}

interface ResultCreationParams {
  group: SourceGroup;
  data: unknown[][];
  status: 'success' | 'pending';
  collectors: Array<{ meta: CollectorMeta<unknown> } | undefined>;
  limit?: number;
  order?: (items: unknown[]) => unknown[];
  getSourceHasNext: (sourceIndex: number) => boolean;
  getSourceNext: (sourceIndex: number) => () => Promise<void>;
  getSourceReset: (sourceIndex: number) => () => void;
}

/**
 * Creates an aggregate fetcher result for a source group
 * @param params - Parameters for creating the result
 * @returns Aggregate fetcher result
 */
export function createGroupResult({
  group,
  data,
  status,
  collectors,
  limit,
  order,
  getSourceHasNext,
  getSourceNext,
  getSourceReset
}: ResultCreationParams): AggregateFetcherResult {
  // Combine data from all sources in the group
  const combinedData = combineGroupData({
    data,
    order,
    sourceIndexes: group.sourceIndexes
  });

  // Check if group has next data
  const hasNext = groupHasNext(group.sourceIndexes, getSourceHasNext);

  // Collect metadata from all sources in the group
  const sourcesStates = collectGroupMetadata(group.sourceIndexes, collectors);

  // Create next and reset functions for the group
  const next = createGroupNext(
    group.sourceIndexes,
    getSourceHasNext,
    getSourceNext
  );
  const reset = createGroupReset(group.sourceIndexes, getSourceReset);

  return {
    id: group.groupId,
    status,
    data: combinedData.slice(0, limit),
    hasNext,
    next,
    reset,
    sourcesStates
  };
}

/**
 * Creates aggregate fetcher results for all source groups
 * @param groups - Array of source groups
 * @param params - Common parameters for all results
 * @returns Array of aggregate fetcher results
 */
export function createAllGroupResults(
  groups: SourceGroup[],
  params: Omit<ResultCreationParams, 'group'>
): AggregateFetcherResult[] {
  return groups.map((group) => createGroupResult({ ...params, group }));
}
